import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { StringEnum } from '../../constants/enums/move-process-enum';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import { formatMessage } from '../../translations/format-message';
import {
  parseDimensionValue,
  getEffectiveDimensionChecked,
  buildCheckItemsPayloadForFileList,
  buildDimensionPayloadForFileList,
  buildMemoPayloadForFileList,
} from './OperatorAssignTask1';

export const useOperatorAssignBatch = (context: any) => {
  const {
    enabled,
    taskId,
    processType,
    OperatorResponse,
    removeForAbnormalityState,
    GetOperatorService,
    CompleteProcessService,
    UpdateProcessService,
    startWebWorkerTimer,
    stopWebWorkerTimer,
    resetWebWorkerTimer,
    onSaveTeplarOrder,
    bulidCheckItemsPayload,
    buildMemoPayload,
    buildDimensionPayload,
    buildBOMCheckListPayload,
    mergedTabs,
    trayHistory,
    setTrayHistory,
    trayIndex,
    setTrayIndex,
    selectedTrayId,
    setSelectedTrayId,
    setActiveIndex,
    setSubmittedCheckItems,
    setMemo,
    setDimensionChecks,
    setCanvasDrawings,
    setBomDataListState,
    startDateTime,
    setStartDateTime,
    startTimer,
    setStartTimer,
    timer,
    setTimer,
    webWorkerTime,
    setWebWorkerTime,
    isTimerRunning,
    setIsTimerRunning,
    isProcessStarted,
    setIsProcessStarted,
    operatorCount,
    setOperatorCount,
    // Scan buffer state is managed via internal refs for batch mode
    traySelectRef,
    areAllTabsChecked,
    isBOMValidated,
    bomDataList,
    setShowDraggableStart,
    resetOperatorResponse,
    buildTeplarListPayload,
    getOperatorState,
    resetGetOperatorState,
    isAddTrayInput,
    setIsAddTrayInput,
    teplarList,
    setTeplarList,
    operatorTeplarDetails,
    CheckProcessStatus,
  } = context;

  const [batchTrayValidation, setBatchTrayValidation] = useState<
    Record<string, boolean>
  >({});
  const [batchData, setBatchData] = useState<Record<string, any>>({});
  const [, setHasLocalBatchRun] = useState(false);
  const [isTrayRemoved, setIsTrayRemoved] = useState(false);

  // Refs for tracking scanner buffer and selected tray during rapid barcode scans
  const scanBufferRef = useRef('');
  const lastKeyTimeRef = useRef(Date.now());
  const trayHistoryRef = useRef<string[]>(trayHistory);
  const selectedTrayIdRef = useRef<string | null>(selectedTrayId);

  // Sync refs with latest state updates
  useEffect(() => {
    trayHistoryRef.current = trayHistory;
  }, [trayHistory]);

  useEffect(() => {
    selectedTrayIdRef.current = selectedTrayId;
  }, [selectedTrayId]);

  const saveTeplarOrderIfNeeded = async () => {
    if (Number(processType) !== 2 || !onSaveTeplarOrder) {
      return;
    }

    await onSaveTeplarOrder();
  };

  useEffect(() => {
    if (!enabled) return;
    setHasLocalBatchRun(false);
    setTimer(0);
    setWebWorkerTime(0);
    setStartTimer(0);
    setIsTimerRunning(false);
  }, [enabled, setIsTimerRunning, setStartTimer, setTimer, setWebWorkerTime]);

  useEffect(() => {
    if (!enabled) return;
    if (trayHistory.length > 0) return;

    setSelectedTrayId(null);
    setTrayIndex(0);
    setActiveIndex(-1);
    setSubmittedCheckItems({});
    setMemo({});
    setDimensionChecks({});
    setCanvasDrawings({});
    setBomDataListState([]);
    resetOperatorResponse();
    setTeplarList([]);
  }, [enabled, trayHistory.length]);

  useEffect(() => {
    if (!enabled) return;
    if (isAddTrayInput && trayHistory.length >= 1) {
      setSelectedTrayId(trayHistory[trayHistory.length - 1]);
      getSelectedTrayDetails(trayHistory[trayHistory.length - 1]);
    }
    if (getOperatorState.success) {
      resetGetOperatorState();
    }
    setIsAddTrayInput(false);
    setIsTrayRemoved(false);
  }, [isAddTrayInput, enabled, getOperatorState.success]);

  useEffect(() => {
    if (!enabled) return;
    if (isTrayRemoved && trayHistory.length >= 1) {
      setSelectedTrayId(trayHistory[trayHistory.length - 1]);
      getSelectedTrayDetails(trayHistory[trayHistory.length - 1]);
    }
    setIsTrayRemoved(false);
  }, [isTrayRemoved, enabled]);

  useEffect(() => {
    if (!enabled) return;
    const handleKeyDown = (e) => {
      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTimeRef.current;

      if (!e.key) return;

      if (
        e.key === 'Shift' ||
        e.key === 'Control' ||
        e.key === 'Alt' ||
        e.key === 'CapsLock' ||
        (e.key.length > 1 && e.key !== 'Enter')
      ) {
        return;
      }

      if (e.key === 'Enter') {
        if (timeDiff > 100) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        const cleanedScan = scanBufferRef.current.trim();
        scanBufferRef.current = '';
        if (cleanedScan === selectedTrayIdRef.current) {
          return;
        }
        if (trayHistoryRef.current.includes(cleanedScan)) {
          getSelectedTrayDetails(cleanedScan);
          traySelectRef.current?.focus();
          return;
        } else if (isProcessStarted) {
          e.preventDefault();
          toast.error(formatMessage('please_complete_the_tray_to_scan'));
          traySelectRef.current?.focus();
          return;
        } else {
          trayHistoryRef.current = [...trayHistoryRef.current, cleanedScan];
          setTrayHistory(trayHistoryRef.current);
          getSelectedTrayDetails(cleanedScan);
          traySelectRef.current?.focus();
          return;
        }
      }

      if (timeDiff > 100) {
        scanBufferRef.current = e.key;
      } else {
        scanBufferRef.current += e.key;
      }

      lastKeyTimeRef.current = currentTime;
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, isProcessStarted]);

  useEffect(() => {
    if (getOperatorState.error) {
      resetGetOperatorState();
      const updatedTrayHistory = trayHistory.filter(
        (trayId: string) => trayId !== selectedTrayId
      );

      setTrayHistory(updatedTrayHistory);
      setSelectedTrayId(updatedTrayHistory[updatedTrayHistory.length - 1]);
      getSelectedTrayDetails(updatedTrayHistory[updatedTrayHistory.length - 1]);
    }
  }, [getOperatorState.error]);

  useEffect(() => {
    if (!enabled) return;
    const hasCurrentTrayResponse =
      OperatorResponse?.TrayId &&
      selectedTrayId &&
      String(OperatorResponse.TrayId) === String(selectedTrayId);

    if (!hasCurrentTrayResponse) {
      if (trayHistory.length === 0) {
        setTimer(0);
      }
      return;
    }

    if (OperatorResponse.CurrentState === StringEnum.Lock) {
      toast.error(formatMessage('partially_processed_error'));
      // setTrayHistory((prev) => prev.slice(0, -1));
      resetOperatorResponse();
    }

    if (OperatorResponse.FileList && OperatorResponse.FileList.length > 0) {
      const isTrue = (val: any) =>
        val === true || String(val).toLowerCase() === 'true';

      let bestIndex = 0;
      let bestPriority = 2;

      OperatorResponse.FileList.forEach((file, index) => {
        let priority = 2;
        if (isTrue(file.IsDefault) || isTrue(file.isDefault)) {
          priority = 0;
        } else if (isTrue(file.IsDimension) || isTrue(file.isDimension)) {
          priority = 1;
        }

        if (priority < bestPriority) {
          bestPriority = priority;
          bestIndex = index;
        }
      });

      setActiveIndex(bestIndex);
    }
  }, [enabled, OperatorResponse, selectedTrayId, trayHistory.length]);

  const checkTrayIsValid = (
    fileList: any[],
    checkItemsState: any,
    dimensionChecksState: Record<string, boolean>,
    bomState: any[],
    procType: any
  ) => {
    let allCheckItemsChecked = true;
    let allDimensionValuesChecked = true;
    let hasAnyDimensionCheckbox = false;

    (fileList ?? []).forEach((fileItem: any, fileIndex: number) => {
      const checkItems = fileItem?.CheckItems ?? [];
      if (checkItems.length > 0) {
        const tabChecks = checkItemsState[fileIndex] || {};
        const allChecked = checkItems.every((group: any) =>
          (group.CheckItemMessage ?? []).every((msg: any) => {
            const checked =
              tabChecks[msg.Id]?.isChecked !== undefined
                ? tabChecks[msg.Id]?.isChecked
                : (msg.IsChecked ?? false);
            return checked === true;
          })
        );
        if (!allChecked) {
          allCheckItemsChecked = false;
        }
      }

      const dimensionsData = fileItem?.DimensionsData ?? [];
      dimensionsData.forEach((dim: any) => {
        if (dim.DimensionType !== 1) return;
        const values = dim.DimensionValues ?? [];
        const realValueRowIndices: number[] = [];
        values.forEach((v: any, idx: number) => {
          const { hasValue } = parseDimensionValue(v);
          if (hasValue) {
            realValueRowIndices.push(idx);
          }
        });
        if (realValueRowIndices.length === 0) return;
        hasAnyDimensionCheckbox = true;
        realValueRowIndices.forEach((rowIndex: number) => {
          const { isDefaultChecked } = parseDimensionValue(values[rowIndex]);
          const checked = getEffectiveDimensionChecked(
            dimensionChecksState,
            fileIndex,
            rowIndex,
            dim.Id,
            isDefaultChecked
          );
          if (!checked) {
            allDimensionValuesChecked = false;
          }
        });
      });
    });

    let bomValid = true;
    if (Number(procType) === 3 && bomState && bomState.length > 0) {
      bomValid = bomState.every(
        (row: any) => !!row && (row.Shortage === true || row.Pick === true)
      );
    }

    return (
      allCheckItemsChecked &&
      (!hasAnyDimensionCheckbox || allDimensionValuesChecked) &&
      bomValid
    );
  };

  useEffect(() => {
    if (!enabled) return;
    if (!OperatorResponse?.FileList?.length) return;
    const respTrayId = OperatorResponse.TrayId;
    if (!respTrayId) return;

    const fileList = OperatorResponse.FileList;
    let currentTrayData = batchData[respTrayId];

    if (!currentTrayData) {
      const hydratedCheckItems: any = {};
      const hydratedMemo: Record<number, string> = {};
      const hydratedDimensions: Record<string, boolean> = {};

      fileList.forEach((fileItem: any, fileIndex: number) => {
        const checkItemsGroups = fileItem?.CheckItems ?? [];
        const memoKey = fileItem?.TabIndex ?? fileIndex;
        const existingMemo =
          Array.isArray(fileItem?.MemoMessage) && fileItem.MemoMessage.length > 0
            ? fileItem.MemoMessage[0]
            : null;

        hydratedMemo[memoKey] = existingMemo?.Memo ?? '';

        if (checkItemsGroups.length > 0) {
          hydratedCheckItems[fileIndex] = {};
          checkItemsGroups.forEach((group: any) => {
            (group?.CheckItemMessage ?? []).forEach((item: any) => {
              if (item.Id != null) {
                hydratedCheckItems[fileIndex][item.Id] = {
                  isChecked: item.IsChecked ?? false,
                  InputMessage: item.InputMessage || '',
                };
              }
            });
          });
        }

        const dims = fileItem?.DimensionsData ?? [];
        dims.forEach((dim: any) => {
          if (dim.DimensionType !== 1) return;
          (dim.DimensionValues ?? []).forEach((val: string, rowIndex: number) => {
            const { isDefaultChecked } = parseDimensionValue(val);
            const key = `${rowIndex}-${dim.Id}`;
            const scopedKey = `${fileIndex}-${rowIndex}-${dim.Id}`;
            hydratedDimensions[scopedKey] = isDefaultChecked;
            hydratedDimensions[key] = isDefaultChecked;
          });
        });
      });

      const initialBom = bomDataList?.length > 0 ? bomDataList : [];

      const initialTeplarList = (operatorTeplarDetails || []).map((item: any) => ({
        ...item,
        isPrinted: item?.isPrinted ?? item?.Printed ?? false,
      }));

      const initialTeplarPayload = initialTeplarList.map((item: any) => ({
        Id: item.Id,
        IsPrinted: Boolean(item.isPrinted || item.Printed),
      }));

      const trayUi = {
        checkItems: hydratedCheckItems,
        dimensionChecks: hydratedDimensions,
        memo: hydratedMemo,
        canvasDrawings: {},
        bomDataListState: initialBom,
        teplarList: initialTeplarList,
      };

      const trayPayload = {
        checkItems: buildCheckItemsPayloadForFileList(fileList, hydratedCheckItems),
        dimensions: buildDimensionPayloadForFileList(fileList, hydratedDimensions),
        memo: buildMemoPayloadForFileList(fileList, hydratedMemo, respTrayId),
        bom: initialBom.map((item: any) => ({
          Id: item.Id,
          Pick: item.Pick ?? false,
          Shortage: item.Shortage ?? false,
        })),
        teplarList: initialTeplarPayload,
      };

      currentTrayData = {
        ui: trayUi,
        payload: trayPayload,
      };

      setBatchData((prev) => ({
        ...prev,
        [respTrayId]: currentTrayData,
      }));

      const isValid = checkTrayIsValid(
        fileList,
        hydratedCheckItems,
        hydratedDimensions,
        initialBom,
        processType
      );

      setBatchTrayValidation((prev) => ({
        ...prev,
        [respTrayId]: isValid,
      }));
    } else {
      const isValid = checkTrayIsValid(
        fileList,
        currentTrayData.ui.checkItems,
        currentTrayData.ui.dimensionChecks,
        currentTrayData.ui.bomDataListState,
        processType
      );
      setBatchTrayValidation((prev) => ({
        ...prev,
        [respTrayId]: isValid,
      }));
    }

    if (String(respTrayId) === String(selectedTrayId)) {
      setSubmittedCheckItems(currentTrayData.ui.checkItems);
      setMemo(currentTrayData.ui.memo);
      setDimensionChecks(currentTrayData.ui.dimensionChecks);
      setCanvasDrawings(currentTrayData.ui.canvasDrawings || {});
      setBomDataListState(currentTrayData.ui.bomDataListState || []);
      setTeplarList(currentTrayData.ui.teplarList || []);
    }
  }, [
    enabled,
    OperatorResponse,
    selectedTrayId,
    bomDataList,
    processType,
    operatorTeplarDetails,
  ]);

  useEffect(() => {
    if (!enabled) return;
    if (selectedTrayId) {
      setBatchTrayValidation((prev) => ({
        ...prev,
        [selectedTrayId]: areAllTabsChecked && isBOMValidated,
      }));
    }
  }, [enabled, areAllTabsChecked, isBOMValidated, selectedTrayId]);

  useEffect(() => {
    if (!enabled) return;
    if (selectedTrayId && batchData[selectedTrayId]?.ui.bomDataListState) {
      setBomDataListState(batchData[selectedTrayId].ui.bomDataListState);
    } else if (bomDataList?.length > 0) {
      setBomDataListState(bomDataList);
    } else {
      setBomDataListState([]);
    }
  }, [enabled, bomDataList, selectedTrayId, batchData]);

  // Restore saved teplar list for selected tray
  useEffect(() => {
    if (!enabled) return;
    if (selectedTrayId && batchData[selectedTrayId]?.ui.teplarList) {
      setTeplarList(batchData[selectedTrayId].ui.teplarList);
    }
  }, [enabled, selectedTrayId, batchData]);

  // Cache current on-screen form state and generated API payload into batchData for specified trayId
  const saveCurrentTrayData = (trayId: string) => {
    setBatchData((prev) => ({
      ...prev,
      [trayId]: {
        ui: {
          checkItems: context.submittedCheckItems,
          dimensionChecks: context.dimensionChecks,
          memo: context.memo,
          canvasDrawings: context.canvasDrawings,
          bomDataListState: context.bomDataListState,
          teplarList: context.teplarList,
        },
        payload: {
          checkItems: bulidCheckItemsPayload(),
          dimensions: buildDimensionPayload(),
          memo: buildMemoPayload(),
          bom: buildBOMCheckListPayload(),
          teplarList: buildTeplarListPayload(),
        },
      },
    }));
  };

  // Clear temporary tray data from localStorage when process completes
  const removeTrayDataOnProcessCompletion = () => {
    localStorage.removeItem('trayData');
  };

  // Cycle active tray selection to next tray in history and fetch its operator details
  const getActiveTrayListServiceHandler = () => {
    const currentTray = trayHistory[trayIndex];
    if (selectedTrayId) {
      saveCurrentTrayData(selectedTrayId);
    }
    selectedTrayIdRef.current = currentTray;
    setSelectedTrayId(currentTray);
    setActiveIndex(-1);
    resetOperatorResponse();
    setSubmittedCheckItems({});
    setMemo({});
    setDimensionChecks({});
    setCanvasDrawings({});
    setTrayIndex((prevIndex) => (prevIndex + 1) % trayHistory.length);
    GetOperatorService(
      Number(getTenantIdFromLocalStorage()),
      currentTray,
      taskId,
      Number(processType)
    );
  };

  // Save current tray data, update selected tray ID ref/state, and fetch tray details via API
  const getSelectedTrayDetails = (trayId: string) => {
    if (selectedTrayId) {
      saveCurrentTrayData(selectedTrayId);
    }
    selectedTrayIdRef.current = trayId;
    setSelectedTrayId(trayId);
    GetOperatorService(
      Number(getTenantIdFromLocalStorage()),
      trayId,
      taskId,
      Number(processType)
    );
  };

  // Remove currently selected tray from batch history before start or clear all when process running
  const handleRemoveTray = () => {
    if (!isProcessStarted) {
      setTrayHistory(() =>
        trayHistory.filter((trayId: string) => trayId !== selectedTrayId)
      );
      setIsTrayRemoved(true);
    } else {
      setTrayHistory([]);
    }
    setShowDraggableStart?.(true);
    resetOperatorResponse();
  };

  // Build complete batch API payload combining active tray state and cached batchData for all trays
  const buildBatchPayload = (isPaused: boolean) => ({
    TenantId: Number(getTenantIdFromLocalStorage()),
    TrayId: trayHistory,
    Count: trayHistory.length,
    ProcessId: taskId,
    StartTime: startDateTime,
    EndTime: new Date().toISOString(),
    Duration: webWorkerTime - startTimer,
    NoOfOperators: operatorCount,
    CurrentState: '',
    IsPaused: isPaused,
    IsBatch: true,
    CheckItemList: trayHistory.flatMap(
      (tid) =>
        (tid === selectedTrayId
          ? bulidCheckItemsPayload()
          : batchData[tid]?.payload.checkItems) || []
    ),
    MemoMessage: trayHistory.flatMap(
      (tid) =>
        (tid === selectedTrayId
          ? buildMemoPayload()
          : batchData[tid]?.payload.memo) || []
    ),
    Dimensions: trayHistory.flatMap(
      (tid) =>
        (tid === selectedTrayId
          ? buildDimensionPayload()
          : batchData[tid]?.payload.dimensions) || []
    ),
    BOMCheckList: trayHistory.flatMap(
      (tid) =>
        (tid === selectedTrayId
          ? buildBOMCheckListPayload()
          : batchData[tid]?.payload.bom) || []
    ),
    TeplarList: trayHistory.flatMap(
      (tid) =>
        (tid === selectedTrayId
          ? buildTeplarListPayload()
          : batchData[tid]?.payload.teplarList) || []
    ),
  });

  // Reset batch state, timers, and validation caches after successful batch process completion
  const completeSuccess = () => {
    setHasLocalBatchRun(false);
    setIsTimerRunning(false);
    setTimer(0);
    resetWebWorkerTimer();
    setOperatorCount(1);
    setIsProcessStarted(false);
    handleRemoveTray();
    setShowDraggableStart?.(true);
    setMemo([]);
    setDimensionChecks([]);
    setSubmittedCheckItems({});
    setCanvasDrawings({});
    setBatchTrayValidation({});
    setBatchData({});
    removeTrayDataOnProcessCompletion();
    resetWebWorkerTimer();
  };

  // Save Teplar order if required, submit batch completion payload to server, and run cleanup
  const handleCompleteProcess = () => {
    saveTeplarOrderIfNeeded();
    const submitObj = buildBatchPayload(false);
    CompleteProcessService(submitObj);
    completeSuccess();
  };

  // Check status of all trays in batch with backend before starting process
  const handleStartClick = async () => {
    const updateObj = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      TrayId: trayHistory?.map((tray) => tray.trim()),
      ProcessId: taskId,
      SetActive: true,
    };
    await CheckProcessStatus(updateObj);
  };

  // Activate batch process state on server, start worker timer, and set process start time
  const handleStartTimer = async () => {
    const updateObj = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      TrayId: trayHistory?.map((tray) => tray.trim()),
      ProcessId: taskId,
      SetActive: true,
    };
    await UpdateProcessService(updateObj);
    setHasLocalBatchRun(true);
    startWebWorkerTimer();
    setStartDateTime(new Date().toISOString());
    setIsTimerRunning(true);
    setIsProcessStarted(true);
    setStartTimer(webWorkerTime);
  };

  // Reset timer state and barcode scan buffer references when process is paused
  const PausedSuccess = () => {
    setHasLocalBatchRun(true);
    setIsTimerRunning(false);
    scanBufferRef.current = '';
    lastKeyTimeRef.current = Date.now();
  };

  // Stop worker timer, save Teplar order if needed, and submit paused process payload
  const handlePauseTimer = () => {
    stopWebWorkerTimer();
    saveTeplarOrderIfNeeded();
    const submitObj = buildBatchPayload(true);
    CompleteProcessService(submitObj);
    PausedSuccess();
  };

  // Deactivate batch process on server, reset timers, and remove current tray
  const removeTrayServiceHandler = () => {
    const obj = {
      TenantId: Number(getTenantIdFromLocalStorage()),
      TrayId: trayHistory?.map((tray) => tray.trim()),
      ProcessId: taskId,
      SetActive: false,
    };
    // UpdateProcessService(obj);
    setHasLocalBatchRun(false);
    setIsProcessStarted(false);
    setIsTimerRunning(false);
    handleRemoveTray();
    setShowDraggableStart?.(true);
    setTimer(0);
    resetWebWorkerTimer();
  };

  // Handle user tray selection change from dropdown menu
  const handleTraySelectChange = (nextTrayId: string) => {
    getSelectedTrayDetails(nextTrayId);
  };

  // Add new tray ID to batch history if not already present and fetch its details
  const addTray = (trayId: string) => {
    if (isTimerRunning) return;
    if (trayHistory.includes(trayId)) {
      setSelectedTrayId(trayId);
      getSelectedTrayDetails(trayId);
      return;
    } else {
      setTrayHistory((prev) => [...prev, trayId]);
      getActiveTrayListServiceHandler();
    }
  };

  // Return true if timer is running and specified tray has incomplete check items or dimensions
  const shouldShowTrayWarning = (tray: string) => {
    if (!isTimerRunning) return false;
    return batchTrayValidation[tray] !== true;
  };

  // Return true if timer is running and specified tray passed all validation checks
  const shouldShowCheckMark = (tray: string) => {
    if (!isTimerRunning) return false;
    return batchTrayValidation[tray] === true;
  };

  const buttons = [
    ...(isTimerRunning
      ? [
          {
            labelKey: 'pause',
            className: 'btn-secondary',
            onClick: handlePauseTimer,
            isDisabled: !isTimerRunning,
          },
          {
            labelKey: 'complete',
            className: 'btn-primary',
            onClick: handleCompleteProcess,
            isDisabled:
              trayHistory.length === 0 ||
              !isProcessStarted ||
              !trayHistory.every((trayId) => batchTrayValidation[trayId]),
          },
        ]
      : trayHistory.length > 0
        ? [
            {
              labelKey: 'start',
              className: 'btn-success',
              onClick: handleStartClick,
              isDisabled: trayHistory.length === 0 || isTimerRunning,
            },
          ]
        : []),
    {
      labelKey: 'remove',
      className: 'btn-danger',
      onClick: removeTrayServiceHandler,
      isDisabled: trayHistory.length === 0 || isTimerRunning,
    },
  ];

  return {
    buttons,
    shouldShowTrayWarning,
    handleTraySelectChange,
    addTray,
    handlePauseTimer,
    shouldShowCheckMark,
    handleStartTimer,
    handleRemoveTray,
    handleStartClick,
    completeSuccess,
    PausedSuccess
  };
};
